import React, { useEffect, useMemo } from 'react';
import { matchPath } from 'react-router-dom';
import Skeleton from 'ui/Skeleton';
import { useGetSkillTemplatesForLessonlyContent } from 'components/Skills/queries/skillTemplates';
import { useGetReadinessSkillsForTaggable } from 'components/Skills/queries/skillTags';
import InfoTooltip from 'components/shared/InfoTooltip';
import Error from 'components/InPersonTraining/shared/Error';
import {
  TEXT_VARIANTS,
  HEADING_VARIANTS,
  Text,
  Heading,
} from 'components/ui/Typography';
import { TYPES, REACT_QUERY_STATUS } from 'constants/config';
import { useFeatureFlags } from 'hooks';

const ViewAssociatedSkills = ({ setSelectedSkills, setSelectedSkillTags }) => {
  const hasSkillsEnabled = useFeatureFlags('readiness-enable-skills-2-0');
  const match = () =>
    matchPath(window.location.pathname, { path: '/lessons/:lessonId' });
  const lessonId = match()?.params?.lessonId;
  const variables = {
    lessonlyContentId: parseInt(lessonId.split('-')[0], 10),
    lessonlyContentType: TYPES.LESSON,
  };

  const { data: skillTemplateData, status } =
    useGetSkillTemplatesForLessonlyContent({
      ...variables,
      skip: hasSkillsEnabled,
    });

  const { data: skillTagData, status: skillTagStatus } =
    useGetReadinessSkillsForTaggable({
      taggableId: parseInt(lessonId.split('-')[0], 10),
      taggableType: TYPES.LESSON,
      skip: !hasSkillsEnabled,
    });

  const skills = useMemo(
    () =>
      skillTemplateData?.skillTemplatesForLessonlyContent?.skillTemplates ?? [],
    [skillTemplateData],
  );

  const skillTags = useMemo(
    () => skillTagData?.readinessSkillsForTaggable ?? [],
    [skillTagData],
  );

  useEffect(() => {
    setSelectedSkills(skills);
  }, [setSelectedSkills, skills]);

  useEffect(() => {
    setSelectedSkillTags(skillTags);
  }, [setSelectedSkillTags, skillTags]);

  if (
    (status === REACT_QUERY_STATUS.LOADING && !hasSkillsEnabled) ||
    (skillTagStatus === REACT_QUERY_STATUS.LOADING && hasSkillsEnabled)
  ) {
    return (
      <div className="Module EditableModule f-1" style={{ marginRight: 204 }}>
        <div className="mb-16">
          <Heading component="h4" variant={HEADING_VARIANTS.H4}>
            <Skeleton />
          </Heading>
        </div>
        <Text component="p" variant={TEXT_VARIANTS.BODY}>
          <Skeleton width={150} />
        </Text>
        <Text component="p" variant={TEXT_VARIANTS.BODY}>
          <Skeleton width={180} />
        </Text>
        <Text component="p" variant={TEXT_VARIANTS.BODY}>
          <Skeleton width={150} />
        </Text>
      </div>
    );
  }

  if (hasSkillsEnabled) {
    if (skillTagStatus === REACT_QUERY_STATUS.ERROR) {
      return (
        <Error
          message={t('skills.associated_skills.error_loading_skill_tags')}
        />
      );
    }
    if (skillTagStatus === REACT_QUERY_STATUS.SUCCESS) {
      return (
        <>
          {skillTags?.length === 0 && (
            <Text className="Placeholder" variant={TEXT_VARIANTS.BODY_LIGHT}>
              {t('skills.associated_skills.no_skills_associated')}
            </Text>
          )}
          {skillTags?.map(skillTag => (
            <div className="d-f fd-r pb-4" key={skillTag.id}>
              <Text variant={TEXT_VARIANTS.BODY} className="mr-8">
                {skillTag.name}
              </Text>
              {skillTag.description && (
                <InfoTooltip
                  className="c-gray-light"
                  tooltip={skillTag.description}
                />
              )}
            </div>
          ))}
        </>
      );
    }
  }

  return (
    <>
      {status === REACT_QUERY_STATUS.ERROR && (
        <Error message={t('skills.associated_skills.error_loading_skills')} />
      )}
      {status === REACT_QUERY_STATUS.SUCCESS && (
        <>
          {skills?.length === 0 && (
            <Text className="Placeholder" variant={TEXT_VARIANTS.BODY_LIGHT}>
              {t('skills.associated_skills.no_skills_associated')}
            </Text>
          )}
          {skills?.map(skill => (
            <div className="d-f fd-r pb-4" key={skill.id}>
              <Text variant={TEXT_VARIANTS.BODY} className="mr-8">
                {skill.name}
              </Text>
              {skill.description && (
                <InfoTooltip
                  className="c-gray-light"
                  tooltip={skill.description}
                />
              )}
            </div>
          ))}
        </>
      )}
    </>
  );
};

export default ViewAssociatedSkills;
